import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Skeleton,
    Tabs,
    Tab,
    Alert
} from '@mui/material';
import {
    Save as SaveIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { expenseTypesAPI, categoriesAPI, paymentModesAPI } from '../api/api';
import { LABELS } from '../config';

const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

const Settings = () => {
    const { user } = useAuth();
    const { settings, updateSettings, fetchSettings, loading: settingsLoading } = useSettings();
    const [tabValue, setTabValue] = useState(0);
    const [saving, setSaving] = useState(false);

    // Local form state
    const [formData, setFormData] = useState({
        // General Settings
        COMPANY_NAME: '',
        COMPANY_ADDRESS: '',
        COMPANY_PHONE: '',
        COMPANY_EMAIL: '',
        INVOICE_PREFIX: 'INV',
        PO_PREFIX: 'PO',

        // GST Settings
        GST_ENABLED: false,
        CGST_RATE: 2.5,
        SGST_RATE: 2.5,
        HSN_CODE_DEFAULT: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (user && user.role === 'VIEWER') {
            toast.error('You do not have permission to access settings');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Expense Types
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [expenseTypeDialog, setExpenseTypeDialog] = useState(false);
    const [editingExpenseType, setEditingExpenseType] = useState(null);
    const [expenseTypeForm, setExpenseTypeForm] = useState({
        name: '',
        description: '',
        division_basis: 'EQUAL'
    });

    // Categories
    const [categories, setCategories] = useState([]);
    const [categoryDialog, setCategoryDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        type: 'EXPENSE',
        description: ''
    });

    // Payment Modes
    const [paymentModes, setPaymentModes] = useState([]);
    const [paymentModeDialog, setPaymentModeDialog] = useState(false);
    const [editingPaymentMode, setEditingPaymentMode] = useState(null);
    const [paymentModeForm, setPaymentModeForm] = useState({
        name: '',
        code: '',
        is_active: true
    });

    useEffect(() => {
        if (settings) {
            setFormData({
                COMPANY_NAME: settings.COMPANY_NAME || 'Spicy Aliyadans',
                GST_NUMBER: settings.GST_NUMBER || '',
                ADDRESS: settings.ADDRESS || '',
                PHONE: settings.PHONE || '',
                EMAIL: settings.EMAIL || '',
                GST_ENABLED: settings.GST_ENABLED === true || settings.GST_ENABLED === 'true',
                CGST_RATE: parseFloat(settings.CGST_RATE) || 9,
                SGST_RATE: parseFloat(settings.SGST_RATE) || 9,
                HSN_CODE_DEFAULT: settings.HSN_CODE_DEFAULT || '',
                INVOICE_PREFIX: settings.INVOICE_PREFIX || 'INV',
                PO_PREFIX: settings.PO_PREFIX || 'PO'
            });
        }
    }, [settings]);

    useEffect(() => {
        loadExpenseTypes();
        loadCategories();
        loadPaymentModes();
    }, []);

    const loadExpenseTypes = async () => {
        try {
            const response = await expenseTypesAPI.getAll();
            if (response.data.success) {
                setExpenseTypes(response.data.data);
            }
        } catch (error) {
            console.error('Error loading expense types:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoriesAPI.getAll();
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadPaymentModes = async () => {
        try {
            const response = await paymentModesAPI.getAll();
            if (response.data.success) {
                setPaymentModes(response.data.data);
            }
        } catch (error) {
            console.error('Error loading payment modes:', error);
        }
    };

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            const updates = Object.entries(formData).map(([key, value]) => ({
                key,
                value: String(value)
            }));

            const success = await updateSettings(updates);
            if (success) {
                toast.success('Settings saved successfully');
                fetchSettings();
            } else {
                toast.error('Failed to save settings');
            }
        } catch (error) {
            toast.error('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    // Expense Type handlers
    const openExpenseTypeDialog = (et = null) => {
        if (et) {
            setEditingExpenseType(et);
            setExpenseTypeForm({
                name: et.name,
                description: et.description || '',
                division_basis: et.division_basis
            });
        } else {
            setEditingExpenseType(null);
            setExpenseTypeForm({ name: '', description: '', division_basis: 'EQUAL' });
        }
        setExpenseTypeDialog(true);
    };

    const saveExpenseType = async () => {
        if (!expenseTypeForm.name) {
            toast.error('Name is required');
            return;
        }
        try {
            if (editingExpenseType) {
                await expenseTypesAPI.update(editingExpenseType.id, expenseTypeForm);
                toast.success('Expense type updated');
            } else {
                await expenseTypesAPI.create(expenseTypeForm);
                toast.success('Expense type created');
            }
            setExpenseTypeDialog(false);
            loadExpenseTypes();
        } catch (error) {
            toast.error('Failed to save expense type');
        }
    };

    const deleteExpenseType = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Expense Type?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33'
        });
        if (result.isConfirmed) {
            try {
                await expenseTypesAPI.delete(id);
                toast.success('Deleted');
                loadExpenseTypes();
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    // Category handlers
    const openCategoryDialog = (cat = null) => {
        if (cat) {
            setEditingCategory(cat);
            setCategoryForm({
                name: cat.name,
                type: cat.type,
                description: cat.description || ''
            });
        } else {
            setEditingCategory(null);
            setCategoryForm({ name: '', type: 'EXPENSE', description: '' });
        }
        setCategoryDialog(true);
    };

    const saveCategory = async () => {
        if (!categoryForm.name) {
            toast.error('Name is required');
            return;
        }
        try {
            if (editingCategory) {
                await categoriesAPI.update(editingCategory.id, categoryForm);
                toast.success('Category updated');
            } else {
                await categoriesAPI.create(categoryForm);
                toast.success('Category created');
            }
            setCategoryDialog(false);
            loadCategories();
        } catch (error) {
            toast.error('Failed to save category');
        }
    };

    const deleteCategory = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Category?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33'
        });
        if (result.isConfirmed) {
            try {
                await categoriesAPI.delete(id);
                toast.success('Deleted');
                loadCategories();
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    // Payment Mode handlers
    const openPaymentModeDialog = (pm = null) => {
        if (pm) {
            setEditingPaymentMode(pm);
            setPaymentModeForm({
                name: pm.name,
                code: pm.code,
                is_active: pm.is_active === 1 || pm.is_active === true
            });
        } else {
            setEditingPaymentMode(null);
            setPaymentModeForm({ name: '', code: '', is_active: true });
        }
        setPaymentModeDialog(true);
    };

    const savePaymentMode = async () => {
        if (!paymentModeForm.name || !paymentModeForm.code) {
            toast.error('Name and Code are required');
            return;
        }
        try {
            if (editingPaymentMode) {
                await paymentModesAPI.update(editingPaymentMode.id, paymentModeForm);
                toast.success('Payment mode updated');
            } else {
                await paymentModesAPI.create(paymentModeForm);
                toast.success('Payment mode created');
            }
            setPaymentModeDialog(false);
            loadPaymentModes();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save payment mode');
        }
    };

    const deletePaymentMode = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Payment Mode?',
            text: 'This will deactivate it if it has linked records.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33'
        });
        if (result.isConfirmed) {
            try {
                await paymentModesAPI.delete(id);
                toast.success('Action successful');
                loadPaymentModes();
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    if (settingsLoading) {
        return (
            <Box>
                <Typography variant="h4" fontWeight={700} gutterBottom>Settings</Typography>
                {[...Array(3)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 2 }} />)}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                Settings
            </Typography>

            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="General" />
                <Tab label="GST & Tax" />
                <Tab label="Expense Types" />
                <Tab label="Categories" />
                <Tab label="Payment Modes" />
            </Tabs>

            {/* General Settings */}
            <TabPanel value={tabValue} index={0}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Company Information</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Company Name"
                                    value={formData.COMPANY_NAME}
                                    onChange={(e) => setFormData({ ...formData, COMPANY_NAME: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="GST Number"
                                    value={formData.GST_NUMBER}
                                    onChange={(e) => setFormData({ ...formData, GST_NUMBER: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address"
                                    value={formData.ADDRESS}
                                    onChange={(e) => setFormData({ ...formData, ADDRESS: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Phone"
                                    value={formData.PHONE}
                                    onChange={(e) => setFormData({ ...formData, PHONE: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={formData.EMAIL}
                                    onChange={(e) => setFormData({ ...formData, EMAIL: e.target.value })}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom>Document Numbering</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Invoice Prefix"
                                    value={formData.INVOICE_PREFIX}
                                    onChange={(e) => setFormData({ ...formData, INVOICE_PREFIX: e.target.value })}
                                    fullWidth
                                    helperText="e.g., INV, SA-INV"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="PO Prefix"
                                    value={formData.PO_PREFIX}
                                    onChange={(e) => setFormData({ ...formData, PO_PREFIX: e.target.value })}
                                    fullWidth
                                    helperText="e.g., PO, SA-PO"
                                />
                            </Grid>
                        </Grid>

                        {user && user.role !== 'VIEWER' && (
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveSettings}
                                    disabled={saving}
                                >
                                    Save Settings
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>

            {/* GST Settings */}
            <TabPanel value={tabValue} index={1}>
                <Card>
                    <CardContent>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.GST_ENABLED}
                                    onChange={(e) => setFormData({ ...formData, GST_ENABLED: e.target.checked })}
                                />
                            }
                            label="Enable GST on Invoices"
                        />

                        <Alert severity="info" sx={{ my: 2 }}>
                            When enabled, GST will be calculated on invoices. You can also toggle GST per invoice.
                        </Alert>

                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CGST Rate (%)"
                                    type="number"
                                    value={formData.CGST_RATE}
                                    onChange={(e) => setFormData({ ...formData, CGST_RATE: parseFloat(e.target.value) || 0 })}
                                    fullWidth
                                    inputProps={{ min: 0, max: 50, step: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="SGST Rate (%)"
                                    type="number"
                                    value={formData.SGST_RATE}
                                    onChange={(e) => setFormData({ ...formData, SGST_RATE: parseFloat(e.target.value) || 0 })}
                                    fullWidth
                                    inputProps={{ min: 0, max: 50, step: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Default HSN Code"
                                    value={formData.HSN_CODE_DEFAULT}
                                    onChange={(e) => setFormData({ ...formData, HSN_CODE_DEFAULT: e.target.value })}
                                    fullWidth
                                    helperText="Default HSN code for pepper products"
                                />
                            </Grid>
                        </Grid>

                        {user && user.role !== 'VIEWER' && (
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveSettings}
                                    disabled={saving}
                                >
                                    Save Settings
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Expense Types */}
            <TabPanel value={tabValue} index={2}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Expense Types</Typography>
                            {user && user.role !== 'VIEWER' && (
                                <Button startIcon={<AddIcon />} onClick={() => openExpenseTypeDialog()}>
                                    Add Type
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Division Basis</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {expenseTypes.map((et) => (
                                        <TableRow key={et.id}>
                                            <TableCell>{et.name}</TableCell>
                                            <TableCell>{LABELS.divisionBasis[et.division_basis]}</TableCell>
                                            <TableCell>{et.description || '-'}</TableCell>
                                            <TableCell align="center">
                                                {user && user.role !== 'VIEWER' && (
                                                    <>
                                                        <IconButton size="small" onClick={() => openExpenseTypeDialog(et)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => deleteExpenseType(et.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Categories */}
            <TabPanel value={tabValue} index={3}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Accounting Categories</Typography>
                            {user && user.role !== 'VIEWER' && (
                                <Button startIcon={<AddIcon />} onClick={() => openCategoryDialog()}>
                                    Add Category
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categories.map((cat) => (
                                        <TableRow key={cat.id}>
                                            <TableCell>{cat.name}</TableCell>
                                            <TableCell>{LABELS.categoryTypes[cat.type]}</TableCell>
                                            <TableCell>{cat.description || '-'}</TableCell>
                                            <TableCell align="center">
                                                {user && user.role !== 'VIEWER' && (
                                                    <>
                                                        <IconButton size="small" onClick={() => openCategoryDialog(cat)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => deleteCategory(cat.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Payment Modes */}
            <TabPanel value={tabValue} index={4}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Payment Modes</Typography>
                            {user && user.role !== 'VIEWER' && (
                                <Button startIcon={<AddIcon />} onClick={() => openPaymentModeDialog()}>
                                    Add Mode
                                </Button>
                            )}
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paymentModes.map((pm) => (
                                        <TableRow key={pm.id}>
                                            <TableCell>{pm.name}</TableCell>
                                            <TableCell><code>{pm.code}</code></TableCell>
                                            <TableCell>
                                                <Box sx={{ color: pm.is_active ? 'success.main' : 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pm.is_active ? 'success.main' : 'error.main' }} />
                                                    {pm.is_active ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                {user && user.role !== 'VIEWER' && (
                                                    <>
                                                        <IconButton size="small" onClick={() => openPaymentModeDialog(pm)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => deletePaymentMode(pm.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {paymentModes.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                No payment modes configured.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </TabPanel>

            {/* Expense Type Dialog */}
            <Dialog open={expenseTypeDialog} onClose={() => setExpenseTypeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingExpenseType ? 'Edit Expense Type' : 'Add Expense Type'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                value={expenseTypeForm.name}
                                onChange={(e) => setExpenseTypeForm({ ...expenseTypeForm, name: e.target.value })}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Division Basis"
                                select
                                value={expenseTypeForm.division_basis}
                                onChange={(e) => setExpenseTypeForm({ ...expenseTypeForm, division_basis: e.target.value })}
                                fullWidth
                            >
                                {Object.entries(LABELS.divisionBasis).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={expenseTypeForm.description}
                                onChange={(e) => setExpenseTypeForm({ ...expenseTypeForm, description: e.target.value })}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExpenseTypeDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveExpenseType}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Category Dialog */}
            <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Type"
                                select
                                value={categoryForm.type}
                                onChange={(e) => setCategoryForm({ ...categoryForm, type: e.target.value })}
                                fullWidth
                            >
                                {Object.entries(LABELS.categoryTypes).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={categoryForm.description}
                                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveCategory}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Payment Mode Dialog */}
            <Dialog open={paymentModeDialog} onClose={() => setPaymentModeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPaymentMode ? 'Edit Payment Mode' : 'Add Payment Mode'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                placeholder="e.g. Cash, Credit Card"
                                value={paymentModeForm.name}
                                onChange={(e) => setPaymentModeForm({ ...paymentModeForm, name: e.target.value })}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Code"
                                placeholder="e.g. CASH, CARD, UPI"
                                value={paymentModeForm.code}
                                onChange={(e) => setPaymentModeForm({ ...paymentModeForm, code: e.target.value })}
                                fullWidth
                                required
                                helperText="Machine-readable code. Use uppercase, no spaces (e.g. GOOGLE_PAY)."
                                disabled={!!editingPaymentMode}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={paymentModeForm.is_active}
                                        onChange={(e) => setPaymentModeForm({ ...paymentModeForm, is_active: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentModeDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={savePaymentMode}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Settings;
